import React, { useState, useEffect } from 'react';
import { Database, Mail, Save, TestTube, Server, Download, Trash2, Play, Plus, X, Pencil, Tag, Star, Check } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('database');
  const [dbForm, setDbForm] = useState({ server: '', name: '', user: '', password: '', port: 1433, encrypt: false });
  const [smtpForm, setSmtpForm] = useState({ host: '', port: 587, username: '', password: '', fromEmail: '', fromName: '', useSsl: true });
  const [testEmail, setTestEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [extConfigs, setExtConfigs] = useState([]);
  const [extForm, setExtForm] = useState({ name: '', server: '', databaseName: '', username: '', password: '', port: 1433, encrypt: false, query: '' });
  const [showExtForm, setShowExtForm] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editingExtId, setEditingExtId] = useState(null);
  const [tags, setTags] = useState([]);
  const [tagForm, setTagForm] = useState({ name: '', color: '#3b82f6' });
  const [smtpAccounts, setSmtpAccounts] = useState([]);
  const [smtpAccountForm, setSmtpAccountForm] = useState({ name: '', host: '', port: 587, username: '', password: '', fromEmail: '', fromName: '', useSsl: true, isDefault: false });
  const [showSmtpAccountForm, setShowSmtpAccountForm] = useState(false);
  const [editingSmtpId, setEditingSmtpId] = useState(null);
  const [smtpTestEmail, setSmtpTestEmail] = useState('');

  useEffect(() => {
    loadSettings();
    loadExtConfigs();
    loadTags();
    loadSmtpAccounts();
  }, []);

  const loadSettings = async () => {
    try {
      const [dbRes, smtpRes] = await Promise.all([
        api.get('/settings/database'),
        api.get('/settings/smtp')
      ]);
      setDbForm(dbRes.data);
      setSmtpForm(smtpRes.data);
    } catch (err) {}
  };

  const saveDb = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.put('/settings/database', dbForm);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata');
    }
    setLoading(false);
  };

  const saveSmtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.put('/settings/smtp', smtpForm);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata');
    }
    setLoading(false);
  };

  const loadSmtpAccounts = async () => {
    try { const res = await api.get('/settings/smtp-accounts'); setSmtpAccounts(res.data); } catch (err) {}
  };

  const saveSmtpAccount = async (e) => {
    e.preventDefault();
    try {
      if (editingSmtpId) {
        await api.put(`/settings/smtp-accounts/${editingSmtpId}`, smtpAccountForm);
        toast.success('SMTP hesabı güncellendi');
      } else {
        await api.post('/settings/smtp-accounts', smtpAccountForm);
        toast.success('SMTP hesabı oluşturuldu');
      }
      setShowSmtpAccountForm(false);
      setEditingSmtpId(null);
      setSmtpAccountForm({ name: '', host: '', port: 587, username: '', password: '', fromEmail: '', fromName: '', useSsl: true, isDefault: false });
      loadSmtpAccounts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata');
    }
  };

  const deleteSmtpAccount = async (id) => {
    if (!window.confirm('Bu SMTP hesabını silmek istiyor musunuz?')) return;
    try {
      await api.delete(`/settings/smtp-accounts/${id}`);
      toast.success('SMTP hesabı silindi');
      loadSmtpAccounts();
    } catch (err) { toast.error('Silinemedi'); }
  };

  const testSmtpAccount = async (id) => {
    try {
      const res = await api.post(`/settings/smtp-accounts/${id}/test`, { email: smtpTestEmail });
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.error || 'Test başarısız'); }
  };

  const loadExtConfigs = async () => {
    try { const res = await api.get('/external/config'); setExtConfigs(res.data); } catch (err) {}
  };

  const editExtConfig = (cfg) => {
    setExtForm({
      name: cfg.Name,
      server: cfg.Server,
      databaseName: cfg.DatabaseName,
      username: cfg.Username || '',
      password: '',
      port: cfg.Port || 1433,
      encrypt: !!cfg.Encrypt,
      query: cfg.Query || ''
    });
    setEditingExtId(cfg.Id);
    setShowExtForm(true);
  };

  const saveExtConfig = async (e) => {
    e.preventDefault();
    try {
      if (editingExtId) {
        await api.put(`/external/config/${editingExtId}`, extForm);
        toast.success('Bağlantı güncellendi!');
      } else {
        await api.post('/external/config', extForm);
        toast.success('Bağlantı kaydedildi!');
      }
      setShowExtForm(false);
      setEditingExtId(null);
      setExtForm({ name: '', server: '', databaseName: '', username: '', password: '', port: 1433, encrypt: false, query: '' });
      loadExtConfigs();
    } catch (err) { toast.error(err.response?.data?.error || 'Hata'); }
  };

  const deleteExtConfig = async (id) => {
    try { await api.delete(`/external/config/${id}`); toast.success('Silindi'); loadExtConfigs(); } catch (err) { toast.error('Silinemedi'); }
  };

  const fetchPreview = async (configId) => {
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const res = await api.post('/external/fetch-preview', { configId });
      setPreviewData(res.data);
      toast.success(`${res.data.count} kayıt bulundu`);
    } catch (err) { toast.error(err.response?.data?.error || 'Sorgu başarısız'); }
    setPreviewLoading(false);
  };

  const importCustomers = async () => {
    if (!previewData?.data) return;
    try {
      const res = await api.post('/external/import-customers', { customers: previewData.data });
      toast.success(res.data.message);
      setPreviewData(null);
    } catch (err) { toast.error(err.response?.data?.error || 'İçe aktarma başarısız'); }
  };

  const loadTags = async () => {
    try {
      const res = await api.get('/tags');
      setTags(res.data);
    } catch (err) {}
  };

  const createTag = async (e) => {
    e.preventDefault();
    if (!tagForm.name.trim()) return toast.error('Etiket adı zorunlu');
    try {
      await api.post('/tags', tagForm);
      toast.success('Etiket oluşturuldu!');
      setTagForm({ name: '', color: '#3b82f6' });
      loadTags();
    } catch (err) { toast.error(err.response?.data?.error || 'Hata'); }
  };

  const deleteTag = async (id) => {
    if (!window.confirm('Bu etiket silinecek. Emin misiniz?')) return;
    try {
      await api.delete(`/tags/${id}`);
      toast.success('Etiket silindi');
      loadTags();
    } catch (err) { toast.error('Silinemedi'); }
  };

  const testSmtp = async () => {
    setLoading(true);
    try {
      const res = await api.post('/settings/smtp/test', { email: testEmail || smtpForm.fromEmail });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Test başarısız');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">Sistem Ayarları</h1>

      {/* Tabs */}
      <div className="flex gap-2">
        <button onClick={() => setActiveTab('database')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${activeTab === 'database' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
          <Database size={18} /> Veritabanı
        </button>
        <button onClick={() => setActiveTab('smtp')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${activeTab === 'smtp' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
          <Mail size={18} /> SMTP Hesapları
        </button>
        <button onClick={() => setActiveTab('external')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${activeTab === 'external' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
          <Download size={18} /> Müşteri İçe Aktar
        </button>
        <button onClick={() => setActiveTab('tags')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${activeTab === 'tags' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
          <Tag size={18} /> Etiketler
        </button>
      </div>

      {/* Database Settings */}
      {activeTab === 'database' && (
        <form onSubmit={saveDb} className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 space-y-5">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Server size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Veritabanı Bağlantısı</h2>
              <p className="text-sm text-slate-500">MSSQL bağlantı ayarları</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Sunucu</label>
              <input type="text" value={dbForm.server} onChange={e => setDbForm({...dbForm, server: e.target.value})}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="localhost" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Veritabanı Adı</label>
              <input type="text" value={dbForm.name} onChange={e => setDbForm({...dbForm, name: e.target.value})}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="TicketDB" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Kullanıcı</label>
              <input type="text" value={dbForm.user} onChange={e => setDbForm({...dbForm, user: e.target.value})}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="sa" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Şifre</label>
              <input type="password" value={dbForm.password} onChange={e => setDbForm({...dbForm, password: e.target.value})}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Port</label>
              <input type="number" value={dbForm.port} onChange={e => setDbForm({...dbForm, port: parseInt(e.target.value)})}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer pb-2">
                <input type="checkbox" checked={dbForm.encrypt} onChange={e => setDbForm({...dbForm, encrypt: e.target.checked})}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-slate-700">Şifreli Bağlantı</span>
              </label>
            </div>
          </div>

          <button type="submit" disabled={loading}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50">
            <Save size={18} /> Kaydet ve Test Et
          </button>
        </form>
      )}

      {/* SMTP Accounts */}
      {activeTab === 'smtp' && (
        <div className="space-y-5">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Mail size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">SMTP Hesapları</h2>
                  <p className="text-sm text-slate-500">Birden fazla gönderim hesabı tanımlayın, personellere atayın</p>
                </div>
              </div>
              <button onClick={() => { setShowSmtpAccountForm(true); setEditingSmtpId(null); setSmtpAccountForm({ name: '', host: '', port: 587, username: '', password: '', fromEmail: '', fromName: '', useSsl: true, isDefault: false }); }}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700">
                <Plus size={16} /> Yeni Hesap
              </button>
            </div>

            {/* SMTP Account List */}
            {smtpAccounts.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Henüz SMTP hesabı tanımlanmamış. Yeni hesap ekleyin.</p>
            ) : (
              <div className="space-y-3">
                {smtpAccounts.map(acc => (
                  <div key={acc.Id} className={`rounded-xl border p-4 ${acc.IsDefault ? 'border-purple-300 bg-purple-50/30' : 'border-slate-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold ${acc.IsDefault ? 'bg-gradient-to-br from-purple-500 to-purple-600' : 'bg-gradient-to-br from-slate-400 to-slate-500'}`}>
                          {acc.IsDefault ? <Star size={16} /> : <Mail size={16} />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-slate-800">{acc.Name}</h3>
                            {acc.IsDefault && <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">Varsayılan</span>}
                          </div>
                          <p className="text-xs text-slate-500">{acc.FromEmail} • {acc.Host}:{acc.Port}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => { setEditingSmtpId(acc.Id); setSmtpAccountForm({ name: acc.Name, host: acc.Host, port: acc.Port, username: acc.Username, password: '********', fromEmail: acc.FromEmail, fromName: acc.FromName || '', useSsl: acc.UseSsl, isDefault: acc.IsDefault }); setShowSmtpAccountForm(true); }}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Pencil size={14} /></button>
                        <button onClick={() => testSmtpAccount(acc.Id)} className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Test"><TestTube size={14} /></button>
                        <button onClick={() => deleteSmtpAccount(acc.Id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SMTP Account Form Modal */}
          {showSmtpAccountForm && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <form onSubmit={saveSmtpAccount} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-800">{editingSmtpId ? 'SMTP Hesabı Düzenle' : 'Yeni SMTP Hesabı'}</h3>
                  <button type="button" onClick={() => { setShowSmtpAccountForm(false); setEditingSmtpId(null); }} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Hesap Adı *</label>
                  <input type="text" value={smtpAccountForm.name} onChange={e => setSmtpAccountForm({...smtpAccountForm, name: e.target.value})} required
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" placeholder="Örn: Destek Mail" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">SMTP Sunucu *</label>
                    <input type="text" value={smtpAccountForm.host} onChange={e => setSmtpAccountForm({...smtpAccountForm, host: e.target.value})} required
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" placeholder="smtp.gmail.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Port</label>
                    <input type="number" value={smtpAccountForm.port} onChange={e => setSmtpAccountForm({...smtpAccountForm, port: parseInt(e.target.value)})}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Kullanıcı Adı *</label>
                    <input type="text" value={smtpAccountForm.username} onChange={e => setSmtpAccountForm({...smtpAccountForm, username: e.target.value})} required
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Şifre *</label>
                    <input type="password" value={smtpAccountForm.password} onChange={e => setSmtpAccountForm({...smtpAccountForm, password: e.target.value})} required
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Gönderen E-posta *</label>
                    <input type="email" value={smtpAccountForm.fromEmail} onChange={e => setSmtpAccountForm({...smtpAccountForm, fromEmail: e.target.value})} required
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" placeholder="destek@firma.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Gönderen Adı</label>
                    <input type="text" value={smtpAccountForm.fromName} onChange={e => setSmtpAccountForm({...smtpAccountForm, fromName: e.target.value})}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm" placeholder="DSY Destek" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={smtpAccountForm.useSsl} onChange={e => setSmtpAccountForm({...smtpAccountForm, useSsl: e.target.checked})}
                      className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500" />
                    <span className="text-sm text-slate-700">SSL/TLS</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={smtpAccountForm.isDefault} onChange={e => setSmtpAccountForm({...smtpAccountForm, isDefault: e.target.checked})}
                      className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500" />
                    <span className="text-sm text-slate-700">Varsayılan Hesap</span>
                  </label>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => { setShowSmtpAccountForm(false); setEditingSmtpId(null); }}
                    className="px-4 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50">Vazgeç</button>
                  <button type="submit" className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700">
                    <Save size={16} /> {editingSmtpId ? 'Güncelle' : 'Oluştur'}
                  </button>
                </div>
              </form>
            </div>
          )}

        </div>
      )}

      {/* External Customer Import */}
      {activeTab === 'external' && (
        <div className="space-y-5">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <Download size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">Harici Müşteri İçe Aktarma</h2>
                  <p className="text-sm text-slate-500">Mevcut MSSQL veritabanından müşterileri çekin</p>
                </div>
              </div>
              <button onClick={() => setShowExtForm(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
                <Plus size={16} /> Yeni Bağlantı
              </button>
            </div>

            {/* Existing Configs */}
            <div className="space-y-3">
              {extConfigs.map(cfg => (
                <div key={cfg.Id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-800">{cfg.Name}</p>
                    <p className="text-xs text-slate-500">{cfg.Server} / {cfg.DatabaseName}</p>
                    {cfg.Query && <p className="text-xs text-slate-400 mt-1 font-mono truncate">{cfg.Query.substring(0, 80)}...</p>}
                  </div>
                  <button onClick={() => fetchPreview(cfg.Id)} disabled={previewLoading}
                    className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-50">
                    <Play size={14} /> Çek
                  </button>
                  <button onClick={() => editExtConfig(cfg)} className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg" title="Düzenle">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => deleteExtConfig(cfg.Id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {extConfigs.length === 0 && <p className="text-center text-slate-400 py-6 text-sm">Henüz harici bağlantı tanımlanmamış</p>}
            </div>
          </div>

          {/* Preview Data */}
          {previewData && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-slate-800">Önizleme ({previewData.count} kayıt)</h3>
                <div className="flex gap-2">
                  <button onClick={importCustomers}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
                    <Download size={16} /> İçe Aktar
                  </button>
                  <button onClick={() => setPreviewData(null)} className="px-3 py-2 text-slate-500 hover:bg-slate-100 rounded-xl text-sm">Kapat</button>
                </div>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>{previewData.data[0] && Object.keys(previewData.data[0]).map(k => (
                      <th key={k} className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">{k}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {previewData.data.slice(0, 50).map((row, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        {Object.values(row).map((v, j) => <td key={j} className="px-3 py-2 text-slate-700 truncate max-w-[200px]">{String(v ?? '')}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* New Config Modal */}
          {showExtForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
                  <h3 className="text-lg font-semibold text-slate-800">{editingExtId ? 'Bağlantıyı Düzenle' : 'Yeni Harici Bağlantı'}</h3>
                  <button onClick={() => { setShowExtForm(false); setEditingExtId(null); setExtForm({ name: '', server: '', databaseName: '', username: '', password: '', port: 1433, encrypt: false, query: '' }); }} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
                </div>
                <form onSubmit={saveExtConfig} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Bağlantı Adı *</label>
                    <input type="text" value={extForm.name} onChange={e => setExtForm({...extForm, name: e.target.value})} required
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Logo Tiger DB" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Sunucu *</label>
                      <input type="text" value={extForm.server} onChange={e => setExtForm({...extForm, server: e.target.value})} required
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="192.168.1.100" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Veritabanı *</label>
                      <input type="text" value={extForm.databaseName} onChange={e => setExtForm({...extForm, databaseName: e.target.value})} required
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Kullanıcı</label>
                      <input type="text" value={extForm.username} onChange={e => setExtForm({...extForm, username: e.target.value})}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Şifre</label>
                      <input type="password" value={extForm.password} onChange={e => setExtForm({...extForm, password: e.target.value})}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Port</label>
                      <input type="number" value={extForm.port} onChange={e => setExtForm({...extForm, port: parseInt(e.target.value)})}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer pb-2">
                        <input type="checkbox" checked={extForm.encrypt} onChange={e => setExtForm({...extForm, encrypt: e.target.checked})}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600" />
                        <span className="text-sm text-slate-700">Şifreli</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">SQL Sorgusu *</label>
                    <textarea value={extForm.query} onChange={e => setExtForm({...extForm, query: e.target.value})} rows={4}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm resize-none"
                      placeholder="SELECT UNVAN as Name, EPOSTA as Email, TELEFON as Phone FROM CARI_HESAPLAR WHERE TIP = 1" />
                    <p className="text-xs text-slate-400 mt-1">Sorgu sonucunda Name (veya UNVAN), Email (veya EPOSTA), Phone (veya TELEFON) sütunları beklenilir.</p>
                  </div>
                  <button type="submit" className="w-full py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700">{editingExtId ? 'Güncelle' : 'Kaydet'}</button>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Tags Management */}
      {activeTab === 'tags' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 space-y-5">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Tag size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Etiket Yönetimi</h2>
              <p className="text-sm text-slate-500">Ticket etiketlerini oluşturun ve yönetin</p>
            </div>
          </div>

          {/* Create Tag Form */}
          <form onSubmit={createTag} className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">Etiket Adı</label>
              <input type="text" value={tagForm.name} onChange={e => setTagForm({...tagForm, name: e.target.value})}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Örn: Acil, Bug, Özellik..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Renk</label>
              <input type="color" value={tagForm.color} onChange={e => setTagForm({...tagForm, color: e.target.value})}
                className="w-12 h-[42px] border border-slate-200 rounded-xl cursor-pointer" />
            </div>
            <button type="submit" className="px-5 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors flex items-center gap-2">
              <Plus size={16} /> Ekle
            </button>
          </form>

          {/* Tags List */}
          <div className="space-y-2">
            {tags.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Henüz etiket yok</p>}
            {tags.map(tag => (
              <div key={tag.Id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: tag.Color }} />
                  <span className="text-sm font-medium text-slate-700">{tag.Name}</span>
                  <span className="text-xs text-slate-400 bg-white px-2 py-0.5 rounded-full">{tag.TicketCount} ticket</span>
                </div>
                <button onClick={() => deleteTag(tag.Id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
